import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import { Logo } from '../../features/brand/Primitives';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/404 — branded not-found page. Catches anything under /unified/*
 * that doesn't have a route. Offers smart suggestions based on the URL +
 * role-aware primary CTA (dashboard for logged-in, start for anonymous).
 */

const EASE = [0.22, 1, 0.36, 1];

// Common routes users might have been aiming for — pair them with a keyword
// match on the requested URL so we can suggest the likely target.
const ROUTE_HINTS = [
  { keys: ['discover', 'browse', 'search', 'find'],      label: 'Find restaurants', to: '/unified/discover' },
  { keys: ['dashboard', 'home', 'profile', 'account'],   label: 'Your dashboard',   to: '/unified/dashboard' },
  { keys: ['chat', 'menu', 'ask'],                       label: 'Chat with a menu', to: '/unified/discover' },
  { keys: ['price', 'pricing', 'plans', 'billing'],      label: 'See pricing',      to: '/unified/pricing' },
  { keys: ['restaurant', 'owner', 'merchant', 'admin'],  label: 'For restaurants',  to: '/unified/for-restaurants' },
  { keys: ['family', 'table', 'share', 'together'],      label: 'My Table',         to: '/unified/my-table' },
  { keys: ['order', 'checkout', 'cart'],                 label: 'Order history',    to: '/unified/order-history' },
  { keys: ['safety', 'allergy', 'signal'],               label: 'Safety signals',   to: '/unified/safety-signals' },
  { keys: ['question', 'ask', 'help'],                   label: 'Table questions',  to: '/unified/table-questions' },
  { keys: ['how', 'works', 'learn', 'intro'],            label: 'How it works',     to: '/unified/how-it-works' },
  { keys: ['about', 'team', 'mission'],                  label: 'About',            to: '/unified/about' },
];

const FALLBACK_SUGGESTIONS = [
  { label: 'Find restaurants', to: '/unified/discover' },
  { label: 'How it works',     to: '/unified/how-it-works' },
  { label: 'Pricing',          to: '/unified/pricing' },
];

export default function UnifiedNotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId } = useContext(UserContext);

  const path = (location.pathname || '').toLowerCase();
  const suggestions = inferSuggestions(path);
  const isLoggedIn = !!userId;

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '60px 20px 100px', textAlign: 'center', position: 'relative', zIndex: 1 }}>

        {/* Big 404 mark with mascot */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.56, ease: EASE }}
          style={{ marginBottom: 22 }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <div style={{
              fontSize: 160,
              fontWeight: 900,
              letterSpacing: '-0.06em',
              lineHeight: 1,
              color: 'transparent',
              backgroundImage: 'linear-gradient(160deg, #BCE7FF 0%, #5BC2FF 45%, #1E9BFF 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              textShadow: '0 24px 56px rgba(13,71,161,0.14)',
            }}>
              404
            </div>
            <motion.div
              animate={{ y: [0, -10, 0], rotate: [-4, 4, -4] }}
              transition={{ duration: 4.2, ease: 'easeInOut', repeat: Infinity }}
              style={{
                position: 'absolute',
                right: -12, top: -8,
                width: 72, height: 72,
                borderRadius: '50%',
                background: '#fff',
                display: 'grid', placeItems: 'center',
                boxShadow: '0 18px 38px rgba(13,71,161,0.22)',
                border: '1.5px solid rgba(255,255,255,0.95)',
              }}
            >
              <Logo variant="mascot" height={44} alt="" />
            </motion.div>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.section
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.12, ease: EASE }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <CompassIcon size={12} /> PAGE NOT FOUND
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 10px' }}>
            We couldn't find that <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>table</em>.
          </h1>
          <p style={{ maxWidth: 460, margin: '0 auto 22px', fontSize: 15, fontWeight: 500, color: 'var(--cwm-gray-700)', lineHeight: 1.55 }}>
            The link may be off the menu, moved, or temporarily unavailable.
          </p>
          {path && path !== '/' && (
            <div style={{
              display: 'inline-block',
              padding: '6px 14px',
              borderRadius: 999,
              background: 'var(--cwm-gray-50)',
              border: '1px solid var(--cwm-gray-200)',
              color: 'var(--cwm-gray-500)',
              fontSize: 12.5, fontWeight: 700,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              marginBottom: 26,
            }}>
              {location.pathname}
            </div>
          )}
        </motion.section>

        {/* Primary CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.2, ease: EASE }}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginBottom: 32 }}
        >
          <button className="cwm-btn" onClick={() => navigate(isLoggedIn ? '/unified/dashboard' : '/unified')}>
            <HomeIcon size={14} /> {isLoggedIn ? 'Back to dashboard' : 'Back to home'}
          </button>
          <button className="cwm-btn-secondary" onClick={() => navigate(-1)}>
            <ArrowLeftIcon size={12} /> Go back
          </button>
        </motion.div>

        {/* Smart suggestions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.28, ease: EASE }}
          className="cwm-sheet"
          style={{ padding: 22, textAlign: 'left' }}
        >
          <div className="cwm-eyebrow" style={{ marginBottom: 14, textAlign: 'center' }}>
            {suggestions.inferred ? 'YOU MIGHT HAVE MEANT' : 'POPULAR PLACES TO START'}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {suggestions.list.map((s, i) => (
              <motion.a
                key={s.to}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.32 + i * 0.05, ease: EASE }}
                whileHover={{ x: 4 }}
                href={s.to}
                onClick={(e) => { e.preventDefault(); navigate(s.to); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', borderRadius: 14,
                  background: '#F7FBFF', border: '1.5px solid transparent',
                  color: 'var(--cwm-blue-deep)', textDecoration: 'none',
                  fontFamily: 'var(--cwm-font)', fontSize: 14.5, fontWeight: 800,
                  transition: 'all 160ms ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--cwm-blue-light)'; e.currentTarget.style.background = 'var(--cwm-blue-pale)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.background = '#F7FBFF'; }}
              >
                <span>{s.label}</span>
                <ArrowIcon size={12} />
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Help link */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 0.56, delay: 0.5, ease: EASE }}
          style={{ marginTop: 26, fontSize: 13, fontWeight: 600, color: 'var(--cwm-gray-500)' }}
        >
          Still stuck? <a href="mailto:hello@chatwithmenu.com" style={{ color: 'var(--cwm-blue-deep)', fontWeight: 800, textDecoration: 'none' }}>hello@chatwithmenu.com</a>
        </motion.div>
      </div>
    </AppShell>
  );
}

function inferSuggestions(path) {
  const matches = new Map();
  ROUTE_HINTS.forEach((hint) => {
    const matchedKeys = hint.keys.filter((k) => path.includes(k));
    if (matchedKeys.length > 0 && !matches.has(hint.to)) {
      matches.set(hint.to, { ...hint, score: matchedKeys.length });
    }
  });
  const list = Array.from(matches.values()).sort((a, b) => b.score - a.score).slice(0, 4);
  if (list.length > 0) return { list, inferred: true };
  return { list: FALLBACK_SUGGESTIONS, inferred: false };
}

// ---------- icons ----------
function CompassIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M16 8l-2 6-6 2 2-6 6-2z"/></svg>; }
function HomeIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12l9-9 9 9M5 10v10h14V10"/></svg>; }
function ArrowLeftIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>; }
function ArrowIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>; }
